import React, { useCallback, useEffect, useState } from 'react';
import {
  performanceMetricsService,
  CtaPerformanceRow,
  CtaPerformanceSnapshot,
} from '../../../../services/performanceMetricsService';
import { DataTable, DataTableColumn } from '../../shared/DataTable';
import { CtaDetailPanel } from './CtaDetailPanel';
import { CtaCharts } from './CtaCharts';

const fmtCtr = (ctr: number | null): string => (ctr == null ? '—' : `${(ctr * 100).toFixed(1)}%`);

const fmtDate = (iso: string): string => {
  try {
    return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
};

function MetricCard({ label, value, sub, tooltip }: {
  label: string; value: string; sub?: string; tooltip?: string;
}) {
  return (
    <div className="border border-gray-200 rounded-lg px-4 py-3 bg-white" title={tooltip}>
      <div className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">{label}</div>
      <div className="text-2xl font-bold text-gray-900 tabular-nums">{value}</div>
      {sub && <div className="text-xs text-gray-400 mt-0.5">{sub}</div>}
    </div>
  );
}

type TableRow = CtaPerformanceRow & { key: string };

export const CtaPerformance: React.FC = () => {
  const [snapshot, setSnapshot] = useState<CtaPerformanceSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<CtaPerformanceRow | null>(null);

  const load = useCallback(async (refresh: boolean) => {
    refresh ? setRefreshing(true) : setLoading(true);
    setError(null);
    try {
      const snap = await performanceMetricsService.getCtaPerformance(refresh);
      setSnapshot(snap);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load CTA performance');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load(false);
  }, [load]);

  if (loading) {
    return (
      <div className="flex items-center gap-3 text-sm text-gray-500 py-12 justify-center">
        <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-cyan-500" />
        Loading CTA performance… (first load scans all articles, ~10s)
      </div>
    );
  }

  if (error) {
    return <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">{error}</div>;
  }

  if (!snapshot) return null;

  const { totals } = snapshot;
  const rows: TableRow[] = snapshot.ctas.map((c) => ({ ...c, key: c.id }));

  const columns: DataTableColumn<TableRow>[] = [
    {
      key: 'cta',
      header: 'CTA',
      render: (r) => (
        <div className="min-w-0 max-w-md">
          <div className="font-medium text-gray-900 truncate">{r.headline || r.id}</div>
          <div className="flex items-center gap-1.5 mt-0.5">
            <span className="text-[10px] font-mono text-gray-400 truncate">{r.id}</span>
            {!r.in_registry && (
              <span className="px-1 py-0.5 rounded bg-amber-50 text-amber-700 text-[9px] font-medium shrink-0" title="Appears on articles or in GA4 events but has no CTA registry entry">
                not in registry
              </span>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'audience',
      header: 'Audience',
      className: 'w-20',
      render: (r) => <span className="text-xs uppercase text-gray-600">{r.audience || '—'}</span>,
    },
    {
      key: 'funnel',
      header: 'Funnel',
      className: 'w-28',
      render: (r) => (
        <span className="text-xs text-gray-600">{r.funnel_stages.length > 0 ? r.funnel_stages.join(' + ') : '—'}</span>
      ),
    },
    {
      key: 'articles',
      header: 'Articles',
      className: 'w-20 text-right',
      render: (r) => <span className="tabular-nums">{r.articles_count}</span>,
    },
    {
      key: 'slots',
      header: 'Slots',
      className: 'w-16 text-right',
      render: (r) => (
        <span className="tabular-nums text-gray-600" title="Total CTA blocks across articles — one article often carries this CTA in 2–3 positions.">
          {r.placements_count}
        </span>
      ),
    },
    {
      key: 'views',
      header: 'Views',
      className: 'w-20 text-right',
      render: (r) => (
        <span className="tabular-nums" title={`Views since ${snapshot.views_since}`}>{r.views}</span>
      ),
    },
    {
      key: 'clicks',
      header: 'Clicks',
      className: 'w-24 text-right',
      render: (r) => (
        <span title={`${r.clicks} clicks all-time (since ${snapshot.tracking_since}) · ${r.clicks_recent} since view tracking began`}>
          <span className="tabular-nums font-semibold">{r.clicks}</span>
          {r.clicks !== r.clicks_recent && (
            <span className="text-[10px] text-gray-400 ml-1">({r.clicks_recent})</span>
          )}
        </span>
      ),
    },
    {
      key: 'ctr',
      header: 'CTR',
      className: 'w-20 text-right',
      render: (r) => (
        <span
          className="tabular-nums"
          title={`${r.clicks_recent} clicks / ${r.views} views — both since ${snapshot.views_since}, so the rate is not inflated by pre-view-tracking clicks`}
        >
          {fmtCtr(r.ctr)}
        </span>
      ),
    },
  ];

  return (
    <div>
      {/* Data-window note + refresh */}
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs text-gray-500">
          Clicks since {snapshot.tracking_since} · views since {snapshot.views_since} · snapshot {fmtDate(snapshot.generated_at)}
          {totals.unattributed_clicks > 0 && ` · ${totals.unattributed_clicks} unattributed click${totals.unattributed_clicks === 1 ? '' : 's'}`}
        </div>
        <button
          onClick={() => load(true)}
          disabled={refreshing}
          className="text-xs px-3 py-1.5 rounded border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 whitespace-nowrap flex items-center gap-1.5"
        >
          {refreshing && <div className="animate-spin rounded-full h-3 w-3 border-t-2 border-b-2 border-gray-500" />}
          {refreshing ? 'Rescanning…' : 'Refresh data'}
        </button>
      </div>

      {totals.views === 0 && (
        <div className="bg-blue-50 border border-blue-200 text-blue-800 px-3 py-2 rounded mb-3 text-xs">
          CTA view tracking went live July 15, 2026. GA4 reports lag ~24–48h, so views and CTR
          will start populating shortly — clicks have been tracked since May 28.
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-6 gap-3 mb-4">
        <MetricCard label="CTAs" value={String(totals.ctas)} tooltip="All CTAs known from the registry, article placements, or GA4 events." />
        <MetricCard label="Articles with CTAs" value={String(totals.articles_with_ctas)} tooltip="Published articles containing at least one CTA block." />
        <MetricCard label="Placements" value={String(totals.placements)} sub="CTA blocks in articles" tooltip="Total CTA blocks across all articles (an article often has 2–3)." />
        <MetricCard label="Views" value={String(totals.views)} sub={`since ${snapshot.views_since}`} tooltip="GA4 cta_view events: banner at least 50% visible for 1s (IAB viewable-impression standard)." />
        <MetricCard label="Clicks" value={String(totals.clicks)} sub={`all-time · ${totals.clicks_recent} since views`} tooltip={`GA4 cta_click events since ${snapshot.tracking_since}. The smaller number is clicks since view tracking began — the CTR basis.`} />
        <MetricCard label="CTR" value={fmtCtr(totals.ctr)} sub="same-window clicks / views" tooltip={`${totals.clicks_recent} clicks / ${totals.views} views, both since ${snapshot.views_since}. Pre-view-tracking clicks are excluded so the rate is honest.`} />
      </div>

      {/* CTA table */}
      <div className="mb-6">
        <DataTable
          columns={columns}
          rows={rows}
          onRowClick={(r) => setSelected(r)}
          emptyMessage="No CTAs found."
        />
      </div>

      {/* Universe charts */}
      <CtaCharts snapshot={snapshot} />

      {/* Flyout */}
      {selected && <CtaDetailPanel cta={selected} onClose={() => setSelected(null)} />}
    </div>
  );
};
